// @ts-strict-ignore
import { MultiSelect, Tooltip } from '@usealloy/component-library';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef,
} from 'react';

import type {
  Expression,
  ExpressionTerm,
  ExtendedDataRoute,
} from 'flow/store/engine/types';
import {
  selectIsSearchBoxVisible,
  selectNodeLineageForAnchorNode,
  selectWorkflowData,
  selectWorkflowProps,
} from 'flow/store/redux/selectors';
import {
  createActionOutputExpressionItem, createDataRoute,
  groupActionOutputExpressionItem,
  removeActionOutputExpressionItem,
  removeDataRoute,
  ungroupActionOutputExpressionItem,
  updateActionOutputExpressionItem, updateDataRouteValue,
} from 'flow/store/redux/slice';
import { useAppDispatch, useAppSelector } from '~/store/reduxHooks';

import {
  canAddAvailableActionsToDropdown,
  getAvailableActionsAsDropdownOptions,
  getAvailableAttributesAsDropdownOptions,
  getMultiSelectProps,
  getPreviousItemInExpressionScope,
} from './helpers';
import { getIsExpressionOperatorString } from '~/dashboard/views/workflows/store/engine/utils';
import { generatePublicIdHash } from 'flow/service/publicIds';
import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import type { DropdownOption } from '../../NodeEditorDrawer/types';
import {
  CaretPositionSuffixTypes,
  MLAAttributeItemOrigin,
  RuleEditorDropdownMode,
  RuleEditorDropdownModeCategory,
} from '../../NodeEditorDrawer/types';
import { getDropdownModeCategory, getIsCaretPositionAtEnd } from '../../useCaretControl/helpers';
import { groupingOptions, operatorOptions } from '../../NodeEditorDrawer/constants';
import { StyledDropdownContainer } from './styles';
import { CaretIndexType, startingCaretPosition } from '../../useCaretControl/constants';
import { canLinkToNode } from '../../Graph/nodes/BaseNode/NodeConnector/helpers';
import { onEscKeyFromRuleDropdown, getSelectedOption } from '../../helpers';
import { StyledSuperset } from '../../mla/styles';
import { checkIfServiceHasMLAAttributes } from '~/dashboard/views/workflows/service/mla';
import { isAggregationServiceAction } from '~/dashboard/views/workflows/service/services';
import { getAllowedAttributesForPortfolioWorkflow } from '~/flow/utils';
import type { ExpressionBuilderDropdownOptions } from './types';
import type { PendingParenthesisInfo } from '../../useCaretControl/types';
import { getExpressionPathInDotNotationFormat } from '../AttributeExpressionGroup/helpers';
import { closeSearchBoxThunk } from 'flow/store/redux/thunks';
import { useEditorAccess } from 'flow/editor/context';
import { EditorMode } from 'flow/editor/context/constants';
import { isSnippetChildAction } from 'flow/service/actions/snippets';
import { AGGREGATION_EDIT_OPTION_LABEL } from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/constants';
import { buildAggregationAttributeOptions } from '../../NodeEditorDrawer/ThresholdsTab/RuleBuilderContainer/Rule/Threshold/helpers';

function ExpressionBuilderDropdown({
  dataRoute,
  expression,
  expressionPath,
  expressions,
  mode,
  canAddGroupCloseOption = false,
}: {
  dataRoute?: ExtendedDataRoute;
  expression?: ExpressionTerm | Expression;
  expressionPath: string;
  expressions?: Expression;
  mode: RuleEditorDropdownMode;
  canAddGroupCloseOption?: boolean;
}) {
  const dispatch = useAppDispatch();
  const anchorNodeLineage = useAppSelector(selectNodeLineageForAnchorNode);

  const { actions, dataRoutes } = useAppSelector(selectWorkflowData);
  const { aggregationConfig, services, workflow } = useAppSelector(selectWorkflowProps);
  const isSearchBoxVisible = useAppSelector(selectIsSearchBoxVisible);

  const multiSelectRef = useRef(null);

  const {
    morphingCaretPosition,
    setMorphingCaretPosition,
    updateRuleItemRefs,
    getFlattenedCaretPositionIndex,
    setPendingMorphingCaretPositionUpdateValue,
    activeInputValue,
    setActiveInputValue,
    getPreviousCaretPosition,
    getNextCaretPosition,
    isolatedEditingBlock,
    setIsolatedEditingBlock,
    mlaUsedInRules,
    sourceAction,
    ruleEditorFlatCaretIndices,
    setPendingGroupOpeningParenthesisInfo,
    pendingGroupOpeningParenthesisInfo,
    ActionHoverTooltip,
  } = useContext(RuleEditorContext);

  const { editorMode } = useEditorAccess();

  const { publicId, output } = sourceAction;

  // is the input manually typed in / entered?
  const isInputTypedIn = activeInputValue.value.length > 0;

  // flattened path meaning we convert expression path (e.g. [0][1]..) into 0-1 - which is the format we use for caret position / caret indices
  const flatCaretPosition = useMemo(() => getFlattenedCaretPositionIndex({
    itemPath: expressionPath,
    mode,
    type: CaretIndexType.EXPRESSION,
  }), [expressionPath, getFlattenedCaretPositionIndex, mode]);

  useEffect(() => {
    if (multiSelectRef?.current && flatCaretPosition) {
      updateRuleItemRefs({
        path: flatCaretPosition,
        width: multiSelectRef.current.controlRef?.offsetWidth,
      });
    }
  }, [flatCaretPosition, multiSelectRef, updateRuleItemRefs]);

  const canAddGroupOpenOption = useMemo(() => pendingGroupOpeningParenthesisInfo.path === null
    && !getNextCaretPosition({}).newPositionString?.endsWith(CaretPositionSuffixTypes.GROUP_END),
  [pendingGroupOpeningParenthesisInfo.path, getNextCaretPosition]);

  const options = React.useMemo<ExpressionBuilderDropdownOptions>(() => {
    let array: DropdownOption[] = [];
    const combinations = { userDefinedValue: null, operator: false };

    const outgoers = new Set([
      ...anchorNodeLineage.directOutgoers,
      ...anchorNodeLineage.extendedOutgoers,
    ]);
    const allAvailableActions = getAvailableActionsAsDropdownOptions(actions, publicId, outgoers);

    const processExpression = () => {
      switch (typeof expression) {
        case 'undefined': {
          // new expression case
          const previousItemInExpressionsScope = getPreviousItemInExpressionScope({
            expressions, expressionPath, mode, output,
          });

          if (canAddGroupOpenOption) array.push(groupingOptions.open);
          else if (canAddGroupCloseOption) array.push(groupingOptions.close);

          if (canAddAvailableActionsToDropdown(expressions, previousItemInExpressionsScope)) {
            array.push(...allAvailableActions);
          } else {
            array.push(...operatorOptions);
            combinations.operator = true;
          }
          break;
        }
        case 'string': {
          if (getIsExpressionOperatorString(expression)) {
            // operator string
            array = operatorOptions;
            combinations.operator = true;
          } else {
            // non operator string
            array.unshift({
              value: String(expression),
              label: expression,
              meta: { category: 'string' },
            });
            combinations.userDefinedValue = 'string';
          }
          break;
        }
        case 'number': {
          // expression exists; so this is edit mode
          array = [
            {
              value: String(expression),
              label: String(expression),
              meta: { category: 'decimal' },
            },
            ...allAvailableActions,
          ];
          combinations.userDefinedValue = 'decimal';
          combinations.operator = true;
          break;
        }
        default: {
          // do nothing
        }
      }
    };

    const processDataRouteAttribute = () => {
      // the dropdown is for configuring data route attribute
      /*
        Temporarily hiding features for Portfolio Evaluations
        Epic ID: 109173
      */

      const allAvailableAttributesOfSelectedAction = getAvailableAttributesAsDropdownOptions(
        getAllowedAttributesForPortfolioWorkflow(
          workflow.type,
          services[dataRoute?.attribute?.service.internal_name]?.name,
          dataRoute?.attribute?.service.attributes,
        ),
      );

      const isMLA = checkIfServiceHasMLAAttributes(dataRoute.attribute.service.internal_name);
      if (isMLA) {
        const otherAttributes = allAvailableAttributesOfSelectedAction.filter(
          attribute => attribute.value !== String(dataRoute?.attribute.id),
        );
        const isCurrentAttrMLA = dataRoute?.attribute?.multiLevelAttributeFilter
          ?.filterComponents?.length > 0;
        array = [
          {
            label: 'Other Multi-Level Attributes',
            value: undefined,
            options: otherAttributes.filter(
              attribute => attribute.label.includes('Multi-Level') || attribute.meta?.isMLA,
            ),
          },
          {
            label: 'All Other Attributes',
            value: undefined,
            options: allAvailableAttributesOfSelectedAction.filter(
              attribute => !attribute.label.includes('Multi-Level') && !attribute.meta?.isMLA,
            ),
          },
        ];

        if (isCurrentAttrMLA) {
          array.unshift({
            label: 'Edit Attribute',
            value: String(dataRoute?.attribute.id),
            meta: {
              shortenedLabel: dataRoute?.attribute.displayName,
              category: 'attribute',
            },
          });
        }
      } else {
        array = allAvailableAttributesOfSelectedAction;
      }

      const aggregationComponent = dataRoute?.aggregationComponents?.[0];
      if (isAggregationServiceAction(dataRoute.sourceAction)) {
        const objectSlug = aggregationComponent?.objectSlug;
        const objectDisplayName = aggregationConfig?.[objectSlug]?.objectDisplayName;
        array = buildAggregationAttributeOptions(
          array,
          aggregationComponent?.displayName || dataRoute.attribute.displayName,
          dataRoute.attribute.id,
          objectDisplayName,
        );
      }
    };

    switch (mode) {
      case RuleEditorDropdownMode.EXPRESSION:
      case RuleEditorDropdownMode.CARET_EXPRESSION: {
        processExpression();
        break;
      }
      case RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE: {
        processDataRouteAttribute();
        break;
      }
      case RuleEditorDropdownMode.DATAROUTE_SOURCE: {
        // the dropdown is for configuring data route source
        array = allAvailableActions.filter(
          action => canLinkToNode(anchorNodeLineage, action.value),
        );
        break;
      }
      case RuleEditorDropdownMode.GROUP_END: {
        // the dropdown is for configuring group close parenthesis
        array = [groupingOptions.end, groupingOptions.ungroup];
        break;
      }
      case RuleEditorDropdownMode.GROUP_START: {
        // the dropdown is for configuring group open parenthesis
        array = [groupingOptions.start, groupingOptions.ungroup];
        break;
      }
      case RuleEditorDropdownMode.GROUP_START_PENDING: {
        array = [groupingOptions.start, groupingOptions.cancel];
        break;
      }
      default: {
        // do nothing
      }
    }

    return {
      array,
      combinations,
    };
  }, [
    actions, mode, publicId, output, canAddGroupCloseOption, canAddGroupOpenOption,
    expression, expressions, expressionPath, dataRoute,
    aggregationConfig, anchorNodeLineage, services, workflow,
  ]);

  const selectedOption = useMemo(() => {
    const modeMap = {
      [RuleEditorDropdownMode.DATAROUTE_SOURCE]: dataRoute?.sourceAction?.publicId,
      [RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE]: dataRoute?.attribute?.id,
      [RuleEditorDropdownMode.EXPRESSION]: (expression === null || expression === undefined)
        ? null
        : expression,
      [RuleEditorDropdownMode.GROUP_START]: '(',
      [RuleEditorDropdownMode.GROUP_START_PENDING]: '(',
      [RuleEditorDropdownMode.GROUP_END]: ')',
    };

    return getSelectedOption({
      incomingValue: String(modeMap[mode]),
      options: options.array,
    });
  }, [mode, options, dataRoute, expression]);

  const onChange = useCallback(inputValue => {
    const { value, meta } = inputValue;
    // typecast as a number instead of string when necessary
    const parsedValue = isNaN(Number(value)) ? value : Number(value);

    const handleNewExpression = () => {
      switch (meta.category) {
        case 'action': {
          // user clicked a service in the dropdown, so we create data route.
          // when creating data route, that reducer will also update action expression.
          const newDataRoutePublicId = `dataroute__${generatePublicIdHash(20)}`;
          dispatch(createDataRoute({
            path: newDataRoutePublicId,
            value: {
              publicId: newDataRoutePublicId,
              sourceId: value,
              targetId: publicId,
            },
            expressionPath,
            // if insertion is true, we "insert," not add at the end of the expression array
            insertion: mode === RuleEditorDropdownMode.CARET_EXPRESSION,
          }));
          break;
        }
        case 'grouping': {
          if (value === '(') {
            const shouldCreateEmptyGroup = (
              !expressions.length
              || getIsCaretPositionAtEnd(ruleEditorFlatCaretIndices, morphingCaretPosition)
            );
            if (shouldCreateEmptyGroup) {
              dispatch(createActionOutputExpressionItem({
                id: publicId,
                path: expressionPath,
                value: [],
                insertion: expressionPath && mode === RuleEditorDropdownMode.CARET_EXPRESSION,
              }));
              return;
            }

            const prevPosition = getPreviousCaretPosition({}).newPositionString || '';

            // in these cases, we need to start the new grouping before the next expression item
            const isFirstDropdown = morphingCaretPosition === startingCaretPosition;
            const isStartedAfterGrouping = (
              prevPosition.endsWith(CaretPositionSuffixTypes.GROUP_END)
              || prevPosition.endsWith(CaretPositionSuffixTypes.GROUP_START)
            );

            const pendingParanthesisInfo: PendingParenthesisInfo = (
              isFirstDropdown || isStartedAfterGrouping
            ) ? {
                path: getExpressionPathInDotNotationFormat({
                  expressions,
                  itemFlatCaret: getNextCaretPosition({}).newPositionString,
                  workflowInfo: { actions, dataRoutes, services },
                }).path,
                side: 'before',
              }
              : { path: expressionPath, side: 'after' };

            setPendingGroupOpeningParenthesisInfo(pendingParanthesisInfo);
          }
          if (value === ')') {
            dispatch(groupActionOutputExpressionItem({
              id: publicId,
              groupOpeningInfo: pendingGroupOpeningParenthesisInfo,
              groupClosingPath: expressionPath,
            }));
            setPendingGroupOpeningParenthesisInfo({ path: null });
          }
          break;
        }
        default: {
          // if expression path exists, we use that.
          // insertion mode will be set when expression path and mode meets the criteria;
          // this means we just add a new expression item at the end of the expression array
          dispatch(createActionOutputExpressionItem({
            id: publicId,
            path: expressionPath,
            value: parsedValue,
            insertion: expressionPath && mode === RuleEditorDropdownMode.CARET_EXPRESSION,
          }));
        }
      }
    };

    if (mode === RuleEditorDropdownMode.EXPRESSION
      || mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
      if (!expression) {
        handleNewExpression();
      } else if (expressionPath) {
        dispatch(updateActionOutputExpressionItem({
          id: publicId,
          expressionPath,
          value: parsedValue,
        }));
      }
    } else if (meta) {
      switch (meta.category) {
        case 'action': {
          if (dataRoute?.sourceAction && mode === RuleEditorDropdownMode.DATAROUTE_SOURCE) {
            dispatch(updateDataRouteValue(
              { path: `${dataRoute.publicId}.sourceId`, value },
            ));
          }
          break;
        }
        case 'attribute': {
          if (dataRoute?.sourceAction && mode === RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE) {
            if (dataRoute?.attribute?.multiLevelAttributeFilter?.filterComponents?.length > 0
              && inputValue.label === 'Edit Attribute') {
              setIsolatedEditingBlock({
                originPublicId: dataRoute.publicId,
                originType: MLAAttributeItemOrigin.DATAROUTE,
                originCaretIndex: flatCaretPosition,
              });
            } else if (inputValue.label === AGGREGATION_EDIT_OPTION_LABEL) {
              setIsolatedEditingBlock({
                originPublicId: dataRoute.publicId,
                originType: MLAAttributeItemOrigin.DATAROUTE,
                originCaretIndex: flatCaretPosition,
              });
            } else {
              dispatch(updateDataRouteValue(
                { path: `${dataRoute.publicId}.attribute.id`, value: parsedValue },
              ));
            }
          }
          break;
        }
        case 'grouping': {
          if (value === groupingOptions.ungroup.value) {
            dispatch(ungroupActionOutputExpressionItem({ id: publicId, expressionPath }));
          } else if (value === groupingOptions.cancel.value) {
            setPendingGroupOpeningParenthesisInfo({ path: null });
          }
          break;
        }
        default: {
          // do nothing
        }
      }
    }

    // we update caret position accordingly because user changed something, and expect the cursor to be moving to the next.
    // but if the mode was CARET_EXPRESSION, meaning it's gonna insert an item somewhere in between, it may result in index changes.
    // so we store the index of where the cursor should be to pending update state in the context and let it update once after indices are regenerated.
    const { newPositionString, newPositionIndexForDelayedUpdate } = getNextCaretPosition({
      userActionObject: {
        meta: meta?.category,
        value,
        mode,
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  }, [
    actions, dataRoutes, services,
    mode, dataRoute,
    expression, expressionPath,
    dispatch, publicId,
    setPendingMorphingCaretPositionUpdateValue,
    setMorphingCaretPosition,
    getNextCaretPosition,
    flatCaretPosition,
    setIsolatedEditingBlock,
    expressions,
    morphingCaretPosition,
    ruleEditorFlatCaretIndices,
    setPendingGroupOpeningParenthesisInfo,
    pendingGroupOpeningParenthesisInfo,
    getPreviousCaretPosition,
  ]);

  const selectProps = useMemo(() => getMultiSelectProps(
    dataRoute?.sourceAction, mode, mode === RuleEditorDropdownMode.GROUP_START_PENDING,
  ), [dataRoute?.sourceAction, mode]);

  const updateValueUsingActiveInputValue = (inputValue: string) => {
    const isOperatorString = getIsExpressionOperatorString(inputValue);
    const isNumberValue = !isNaN(Number(inputValue));
    const availableOptionsContainOperatorOrUserDefinedString = options?.combinations.operator || options?.combinations.userDefinedValue === 'string';
    const availableOptionsContainActionsOrUserDefinedNumber = options?.combinations.operator || options?.combinations.userDefinedValue === 'string';
    if (
      (availableOptionsContainOperatorOrUserDefinedString && isOperatorString)
      || (availableOptionsContainActionsOrUserDefinedNumber && isNumberValue)
    ) {
      onChange({
        label: inputValue,
        value: inputValue,
        meta: {
          category: isOperatorString ? 'operator' : '',
          textInput: true,
        },
      });
      setActiveInputValue({
        value: '',
        path: '',
        initialValue: false,
      });
    }
  };

  const onInputChange = (inputValue: string) => {
    // we track this to handle dropdown delete key down handling
    setActiveInputValue({
      value: inputValue,
      path: expressionPath,
      initialValue: false,
    });

    if (inputValue.endsWith(' ')) {
      // whitespace at the end, we'll try see if this is just number/operator with a whitespace at the end
      // if so, we'll automatically "submit" this for user convenience
      const trimmedInputValue = inputValue.slice(0, inputValue.length - 1);
      updateValueUsingActiveInputValue(trimmedInputValue);
    }
  };

  const onDeleteItem = (inputValue: string) => {
    // if activeInputValue has something, user should be able to delete indiv character in the dropdown box, not delete the whole item
    if (isInputTypedIn) return;
    if (mode === RuleEditorDropdownMode.EXPRESSION) {
      if ((!inputValue || inputValue?.length <= 0) && (expression !== undefined)) {
        dispatch(removeActionOutputExpressionItem({
          id: publicId,
          expressionPath,
        }));
        setActiveInputValue({
          value: '',
          path: '',
          initialValue: false,
        });
      }
    } else if (getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.DATAROUTE) {
      dispatch(removeDataRoute({
        id: dataRoute.publicId,
        allowEmptyParentGroup: true,
      }));
      setActiveInputValue({
        value: '',
        path: '',
        initialValue: false,
      });
    } else if (getDropdownModeCategory(mode) === RuleEditorDropdownModeCategory.GROUP) {
      dispatch(ungroupActionOutputExpressionItem({ id: publicId, expressionPath }));
    }

    // we update caret position accordingly because user deleted an expression item, and expect the cursor to be moving to the item left to whatever that's now deleted.
    // but things are being deleted and it may result in index changes, especially when group parenthesis is removed. (caret indices are regenerated)
    // so we store the index of where the cursor should be to pending update state in the context and let it update once after indices are regenerated.
    const { newPositionString, newPositionIndexForDelayedUpdate } = getPreviousCaretPosition({
      userActionObject: {
        mode: 'deletion',
      },
    });

    if (newPositionIndexForDelayedUpdate) {
      setPendingMorphingCaretPositionUpdateValue(newPositionIndexForDelayedUpdate);
    } else {
      setMorphingCaretPosition(newPositionString);
    }
  };

  useEffect(() => {
    if (morphingCaretPosition === flatCaretPosition
      || mode === RuleEditorDropdownMode.CARET_EXPRESSION) {
      // focus the dropdown automatically when caret position is pointing here
      multiSelectRef?.current?.focus();
    }
  }, [flatCaretPosition, mode, morphingCaretPosition]);

  const onFocus = () => {
    if (isSearchBoxVisible) {
      dispatch(closeSearchBoxThunk());
    }
    if (morphingCaretPosition !== flatCaretPosition && flatCaretPosition) {
      // update caret position to be here if focused by other ways (mouse click etc)
      setMorphingCaretPosition(`${flatCaretPosition}`);
    }
  };

  const onAltKey = () => setActiveInputValue({
    value: selectedOption?.value || '',
    path: expressionPath,
    initialValue: true,
  });

  const onBlur = () => {
    // if there's any value provided by user, we'll automatically "submit" this for user convenience
    if (isInputTypedIn) updateValueUsingActiveInputValue(activeInputValue.value);
  };

  const stringInputValue = isInputTypedIn
    ? activeInputValue.value
    : selectedOption?.value || '';

  const inputValue = (
    mode === RuleEditorDropdownMode.EXPRESSION
    && activeInputValue.path === expressionPath
    && morphingCaretPosition === flatCaretPosition
  )
    ? stringInputValue
    : undefined;

  const mlaEditorEnabledForAnotherItem = (
    isolatedEditingBlock.originCaretIndex
    && isolatedEditingBlock.originCaretIndex !== flatCaretPosition
  );

  const onEscKey = (_: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    onEscKeyFromRuleDropdown(
      e,
      activeInputValue,
      setActiveInputValue,
      multiSelectRef,
    );
  };

  const attrMLAFootnoteNumber = useMemo(() => (
    dataRoute && mode === RuleEditorDropdownMode.DATAROUTE_ATTRIBUTE
      ? mlaUsedInRules.findIndex(rule => (
        rule.publicId === dataRoute.publicId
        && rule.type === 'dataRoute'
      ))
      : -1
  ), [dataRoute, mode, mlaUsedInRules]);

  // makes case insensitive and ':' agnostic
  const cleanseString = (str: string) => str.replace(':', '').toLowerCase();

  // helper func to filters options
  const includesActiveInput = (option, inputtedValue: string) => (
    cleanseString(option.label).includes(cleanseString(inputtedValue))
  );

  const isDropdownDisabled = Boolean(isolatedEditingBlock.originPublicId)
    || (isSnippetChildAction(sourceAction))
    || ![EditorMode.CheckedOut, EditorMode.Standard]
      .includes(editorMode);

  const tooltipContent = useMemo(() => {
    if (ActionHoverTooltip && [
      RuleEditorDropdownMode.DATAROUTE_SOURCE,
    ].includes(mode) && !Array.isArray(selectedOption) && selectedOption?.value) {
      const hoveredAction = actions[selectedOption.value];
      return (
        <ActionHoverTooltip action={hoveredAction} />
      );
    }

    return null;
  }, [ActionHoverTooltip, actions, mode, selectedOption]);

  return (
    <Tooltip
      align="start"
      content={tooltipContent}
      side="top"
      variant="secondary"
      hide={(
        !tooltipContent
        || (
          selectedOption
          && !Array.isArray(selectedOption)
          && !selectedOption?.meta?.shortenedLabel
        )
        || !selectedOption
        || (Array.isArray(selectedOption) && !selectedOption.length)
      )}
    >
      <StyledDropdownContainer
        isDimmed={mlaEditorEnabledForAnotherItem}
        isTemporary={mode === RuleEditorDropdownMode.GROUP_START_PENDING}
      >
        <MultiSelect
          {...selectProps}
          openMenuOnFocus
          onChange={onChange}
          options={options?.array}
          onFocus={onFocus}
          onBlur={onBlur}
          inputValue={inputValue}
          selectedOptions={selectedOption}
          isCreatable
          isDisabled={isDropdownDisabled}
          placeholder={mode === RuleEditorDropdownMode.CARET_EXPRESSION ? '' : '...'}
          createOptionPosition="first"
          customMenuPortalTarget={document.querySelector('.dashboard-view')}
          onCreateOption={
            input => onChange({ ...input, meta: { ...input?.meta, textInput: true } })
          }
          onKeyDownHandlers={{
            Backspace: onDeleteItem,
            Delete: onDeleteItem,
            Alt: mode === RuleEditorDropdownMode.EXPRESSION ? onAltKey : undefined,
            Escape: onEscKey,
          }}
          onInputChange={onInputChange}
          filterOption={isInputTypedIn ? includesActiveInput : undefined}
          formatCreateLabel={newValue => (`Add ${!isNaN(Number(newValue)) ? 'number:' : 'string:'} ${newValue}`)}
          ref={multiSelectRef}
        />
        {attrMLAFootnoteNumber > -1 ? (
          <StyledSuperset>{attrMLAFootnoteNumber + 1}</StyledSuperset>
        ) : null}
      </StyledDropdownContainer>
    </Tooltip>
  );
}

export default ExpressionBuilderDropdown;
